import {
	Card,
	CardBody,
	CardFooter,
	Content,
	ContentVariants,
	Flex,
	FlexItem,
	Grid,
	GridItem,
	Truncate,
} from "@patternfly/react-core";
import { useQueryClient } from "@tanstack/react-query";
import type { IActiveProductDetails, IProductListDetails } from "../../../../constants/apiconstants-pyxis";
import { listingTypeOptions } from "../../../../constants/products";
import { useProductScoreMetadata } from "../../../../hooks/useProductScoreMetadata";
import Env from "../../../../utils/env";
import { getTypeNameWithIcon } from "../../../../utils/productListUtils";
import "./ProductCard.scss";
import ProductScore from "./ProductScore";

const PublishedProductItem = ({ publishedProduct }: { publishedProduct: IProductListDetails }) => {
	const typeName =
		listingTypeOptions.find((option) => publishedProduct.type && option.value === publishedProduct.type)?.header || "";
	const publishedDate = new Date(publishedProduct?.published_history?.history?.slice(-1)?.[0]?.date).toLocaleDateString(
		"en-US",
		{ year: "numeric", month: "2-digit", day: "2-digit" },
	);
	const { graphValues, scoreStatus } = useProductScoreMetadata(publishedProduct?._id);

	return (
		<GridItem span={4} key={publishedProduct?._id}>
			<Card
				className="products__card"
				component="a"
				href={`${Env.ConnectPortalUrls[Env.getEnvName()]}manage/products/${publishedProduct?._id}`}
				aria-label={`Published Product details card. Name: ${publishedProduct?.name}, Type: ${typeName}, Published date: ${publishedDate}, and Product Score status: ${scoreStatus} `}
				isFullHeight
			>
				<CardBody isFilled>
					<Flex
						alignItems={{ default: "alignItemsFlexStart" }}
						justifyContent={{ default: "justifyContentSpaceBetween" }}
					>
						<FlexItem>
							<Content className="products__card__description">
								<Content className="products__card__description__type" component="p">
									{typeName}
								</Content>
								<Content aria-hidden={true} component="h4">
									<Truncate content={publishedProduct?.name} />
								</Content>
								<Content className="products__card__description__cert-info" component="p">
									Published: {publishedDate}
								</Content>
							</Content>
						</FlexItem>
						<FlexItem>
							<div className="products__card__type-icon">{getTypeNameWithIcon(publishedProduct?.type)}</div>
						</FlexItem>
					</Flex>
				</CardBody>
				<CardFooter className="products__card__footer">
					<ProductScore graphValues={graphValues} scoreStatus={scoreStatus} />
				</CardFooter>
			</Card>
		</GridItem>
	);
};

const PublishedProductCard = () => {
	const publishedProducts = useQueryClient().getQueryData<IActiveProductDetails>(["productList"])?.data
		?.publishedProducts;
	return (
		<div className="products">
			<Content>
				<Content className="products__heading" component={ContentVariants.h3}>
					Recently published products
				</Content>
			</Content>
			<Grid hasGutter>
				{publishedProducts?.data?.map((publishedProduct: IProductListDetails) => (
					<PublishedProductItem key={publishedProduct?._id} publishedProduct={publishedProduct} />
				))}
			</Grid>
		</div>
	);
};

export default PublishedProductCard;
